import React, { useState, useEffect } from 'react';
import { Cookie, X, Settings, CheckCircle, AlertCircle } from 'lucide-react';
import cookieService from './cookieService';

const CookieBanner = ({ onAcceptAll, onEssentialOnly, onClose }) => {
  const [showDetails, setShowDetails] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null); // null, 'success', 'error'
  const [customPreferences, setCustomPreferences] = useState({
    marketing: false,
    analytics: false,
    personalization: false
  });

  // 🎯 MANEJAR ACEPTAR TODAS LAS COOKIES
  const handleAcceptAll = async () => {
    if (isLoading) return; // ✅ Prevenir clicks múltiples
    
    setIsLoading(true);
    setSaveStatus(null);
    
    try {
      const result = await cookieService.savePreferences(true);
      
      if (result.preferences) {
        setSaveStatus('success');
        console.log('✅ Todas las cookies aceptadas');
        
        // Callback opcional
        if (onAcceptAll) onAcceptAll(result);
        
        // Cerrar banner después de un momento
        setTimeout(() => {
          if (onClose) onClose();
        }, 1500);
      } else {
        throw new Error('Error al guardar preferencias');
      }
    } catch (error) {
      console.error('❌ Error al aceptar cookies:', error);
      setSaveStatus('error');
    } finally {
      setIsLoading(false);
    }
  };

  // 🛡️ MANEJAR SOLO COOKIES ESENCIALES
  const handleEssentialOnly = async () => {
    if (isLoading) return; // ✅ Prevenir clicks múltiples
    
    setIsLoading(true);
    setSaveStatus(null);
    
    try {
      const result = await cookieService.savePreferences(false);
      
      if (result.preferences) {
        setSaveStatus('success');
        console.log('✅ Solo cookies esenciales aceptadas');
        
        // Callback opcional
        if (onEssentialOnly) onEssentialOnly(result);
        
        // Cerrar banner después de un momento
        setTimeout(() => {
          if (onClose) onClose();
        }, 1500);
      } else {
        throw new Error('Error al guardar preferencias');
      }
    } catch (error) {
      console.error('❌ Error al guardar cookies esenciales:', error);
      setSaveStatus('error');
    } finally {
      setIsLoading(false);
    }
  };

  // ⚙️ MANEJAR PREFERENCIAS PERSONALIZADAS
  const handleCustomPreferences = async () => {
    if (isLoading) return; // ✅ Prevenir clicks múltiples
    
    setIsLoading(true);
    setSaveStatus(null);
    
    try {
      // Determinar si acepta marketing basado en las preferencias
      const acceptMarketing = customPreferences.marketing;
      
      const result = await cookieService.savePreferences(acceptMarketing, {
        analytics: customPreferences.analytics,
        personalization: customPreferences.personalization
      });
      
      if (result.preferences) {
        setSaveStatus('success');
        console.log('✅ Preferencias personalizadas guardadas:', customPreferences);
        
        // Callback opcional - usar el que corresponda
        const callback = acceptMarketing ? onAcceptAll : onEssentialOnly;
        if (callback) callback(result);
        
        // Cerrar banner después de un momento
        setTimeout(() => {
          if (onClose) onClose();
        }, 1500);
      } else {
        throw new Error('Error al guardar preferencias personalizadas');
      }
    } catch (error) {
      console.error('❌ Error al guardar preferencias personalizadas:', error);
      setSaveStatus('error');
    } finally {
      setIsLoading(false);
    }
  };

  // 🔄 ACTUALIZAR PREFERENCIA INDIVIDUAL
  const updatePreference = (type, value) => {
    setCustomPreferences(prev => ({
      ...prev,
      [type]: value
    }));
  };

  // 🎨 COMPONENTE DE ESTADO DE GUARDADO
  const SaveStatusIndicator = () => {
    if (!saveStatus) return null;
    
    return (
      <div className={`flex items-center gap-2 text-sm ${
        saveStatus === 'success' ? 'text-green-600' : 'text-red-600'
      }`}>
        {saveStatus === 'success' ? (
          <>
            <CheckCircle size={16} />
            <span>Preferencias guardadas exitosamente</span>
          </>
        ) : (
          <>
            <AlertCircle size={16} />
            <span>Error al guardar. Inténtalo de nuevo.</span>
          </>
        )}
      </div>
    );
  };

  // 🎛️ COMPONENTE DE TOGGLE PARA PREFERENCIAS
  const PreferenceToggle = ({ label, description, enabled, onChange, disabled = false }) => (
    <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200">
      <div className="flex-1">
        <h5 className="font-medium text-gray-900">{label}</h5>
        <p className="text-sm text-gray-600 mt-1">{description}</p>
      </div>
      <div className="ml-4">
        <button
          onClick={() => !disabled && onChange(!enabled)}
          disabled={disabled}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
            disabled 
              ? 'bg-gray-300 cursor-not-allowed' 
              : enabled 
                ? 'bg-indigo-600' 
                : 'bg-gray-200'
          }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              enabled ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>
      </div>
    </div>
  );

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 shadow-2xl">
      <div className="max-w-7xl mx-auto p-4">
        {!showDetails ? (
          // 📱 VISTA SIMPLE DEL BANNER
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <Cookie className="w-6 h-6 text-indigo-600 mt-1 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-gray-900">🍪 Utilizamos cookies</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Usamos cookies esenciales para el funcionamiento y opcionales para marketing (remarketing en Facebook/Instagram).
                </p>
                <SaveStatusIndicator />
              </div>
            </div>
            
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => setShowDetails(true)}
                disabled={isLoading}
                className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2 disabled:opacity-50"
              >
                <Settings size={16} />
                Personalizar
              </button>
              <button
                onClick={handleEssentialOnly}
                disabled={isLoading}
                className="px-4 py-2 text-sm bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50"
              >
                {isLoading ? 'Guardando...' : 'Solo esenciales'}
              </button>
              <button
                onClick={handleAcceptAll}
                disabled={isLoading}
                className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50"
              >
                {isLoading ? 'Guardando...' : 'Aceptar todas'}
              </button>
            </div>
          </div>
        ) : (
          // ⚙️ VISTA DETALLADA CON OPCIONES PERSONALIZADAS
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Cookie className="w-5 h-5 text-indigo-600" />
                Configuración de cookies
              </h3>
              <button
                onClick={() => setShowDetails(false)}
                disabled={isLoading}
                className="text-gray-500 hover:text-gray-700 disabled:opacity-50"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="space-y-3">
              {/* Cookies esenciales - siempre activadas */}
              <PreferenceToggle
                label="Cookies esenciales"
                description="Necesarias para el funcionamiento básico (autenticación, seguridad, preferencias)"
                enabled={true}
                onChange={() => {}}
                disabled={true}
              />
              
              {/* Cookies de marketing */}
              <PreferenceToggle
                label="Cookies de marketing"
                description="Para remarketing en Facebook/Instagram y análisis publicitario"
                enabled={customPreferences.marketing}
                onChange={(value) => updatePreference('marketing', value)}
              />
              
              {/* Cookies de análisis */}
              <PreferenceToggle
                label="Cookies de análisis"
                description="Para entender cómo interactúas con nuestro sitio y mejorarlo"
                enabled={customPreferences.analytics}
                onChange={(value) => updatePreference('analytics', value)}
              />
              
              {/* Cookies de personalización */}
              <PreferenceToggle
                label="Cookies de personalización"
                description="Para recordar tus preferencias y personalizar tu experiencia"
                enabled={customPreferences.personalization}
                onChange={(value) => updatePreference('personalization', value)}
              />
            </div>
            
            {/* Estado de guardado */}
            <SaveStatusIndicator />
            
            {/* Botones de acción */}
            <div className="flex flex-wrap gap-3 pt-4 border-t border-gray-200">
              <button
                onClick={handleEssentialOnly}
                disabled={isLoading}
                className="px-6 py-2 text-sm bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50"
              >
                {isLoading ? 'Guardando...' : 'Solo esenciales'}
              </button>
              <button
                onClick={handleCustomPreferences}
                disabled={isLoading}
                className="px-6 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50"
              >
                {isLoading ? 'Guardando...' : 'Guardar preferencias'}
              </button>
              <button
                onClick={handleAcceptAll}
                disabled={isLoading}
                className="px-6 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
              >
                {isLoading ? 'Guardando...' : 'Aceptar todas'}
              </button>
            </div>
            
            <p className="text-xs text-gray-500 pt-2">
              Puedes cambiar estas preferencias en cualquier momento desde la configuración de tu cuenta.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CookieBanner;